import { useState, useEffect } from "react";
import { Tooltip } from "antd";
import BankAccountMultiView from "../../../components/bankAccount/multiView";
import { backendGetRequest, backendGetRequestWithCache, backendPostWithoutRefresh } from "../../../helpers/callBackend";
import BalanceHeaderV3 from "../../../components/header/balanceHeaderV3";
import SelectBankAccountTag from "../../../components/specific/customTags/selectBankAccountTag";
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import Divider from '@mui/material/Divider';
import Skeleton from '@mui/material/Skeleton';
import Button from "@mui/material/Button";
import { cancelAllAxiosRequests } from "../../../helpers/cancelAxiosRequests";
import ExportHierarchyAccount from "../bankAccountsAll/tempComponents/connectedAccounts/tableDropdown/ExportHierarchyAccount";
import GenericDropdown from "../../../components/generic/genericDropdown";
import OuterBox from "../../../components/generic/arpariBoxes/OuterBox";

function BankAccountHierarchy() {
  const [hierarchyAccounts, setHierarchyAccounts] = useState([]);
  const [accountsLoading, setAccountsLoading] = useState(false);
  const [accountsPerPage, setAccountsPerPage] = useState({});
  const [accountsPageCountExpected, setAccountsPageCountExpected] = useState(0);
  const [accountsPageCountReceived, setAccountsPageCountReceived] = useState(-1);
  const [flattenedTags, setFlattenedTags] = useState([]);
  const [flattenedTagsPageCountExpected, setFlattenedTagsPageCountExpected] = useState(0);
  const [flattenedTagsPageCountReceived, setFlattenedTagsPageCountReceived] = useState(-1);
  const [flattenedTagsLoading, setFlattenedTagsLoading] = useState(true);
  const [groups, setGroups] = useState([]);
  const [groupsLoading, setGroupsLoading] = useState(true);
  const [groupsPageCountReceived, setGroupsPageCountReceived] = useState(0);
  const [groupsPageCountExpected, setGroupsPageCountExpected] = useState(-1);

  const [accountCount, setAccountCount] = useState(0)
  const [bankCount, setBankCount] = useState(0)
  const [countLoading, setCountLoading] = useState(true);
  const [shouldFetchAfterClear, setShouldFetchAfterClear] = useState(false);
  const [preventAutoFetch, setPreventAutoFetch] = useState(false);
  const [currentGroupId, setCurrentGroupId] = useState(() => {
    return JSON.parse(window.localStorage.getItem("currentGroupId")) || undefined;
  })
  const [currentTagId, setCurrentTagId] = useState(() => {
    return JSON.parse(window.localStorage.getItem("currentTagId")) || undefined;
  })

  const [bankAccountPurposes, setBankAccountPurposes] = useState(() => {
    return JSON.parse(window.localStorage.getItem("bankAccountPurposes")) || [];
  });

  const [balancesByPurposePageCountExpected, setBalancesByPurposePageCountExpected] = useState({});
  const [balancesByPurposePageCountReceived, setBalancesByPurposePageCountReceived] = useState({});
  const [balancesByPurpose, setBalancesByPurpose] = useState({});
  const [balancesByPurposeIsComplete, setBalancesByPurposeIsComplete] = useState({});

  const [csvModalVisible, setCsvModalVisible] = useState(false);
  const [dropdownItems, setDropdownItems] = useState([]);
  const [findingAccounts, setFindingAccounts] = useState(false);
  const [findNewAccountsPageCountExpected, setFindNewAccountsPageCountExpected] = useState(0);
  const [findNewAccountsPageCountReceived, setFindNewAccountsPageCountReceived] = useState(-1);
  const [selectedDropdownValue, setSelectedDropdownValue] = useState(undefined);

  const BA_TAG = 1;

  useEffect(() => {
    let newDropdownItems = [];
    newDropdownItems.push(
      <Tooltip title={!hierarchyAccounts || (hierarchyAccounts.length === 0) ? "No accounts" : undefined}>
        <a
          onClick={!hierarchyAccounts || (hierarchyAccounts.length === 0) ? undefined : () => setCsvModalVisible(true)}
          style={{ cursor: !hierarchyAccounts || (hierarchyAccounts.length === 0) ? "not-allowed" : "pointer", }}
        >
          Export Data
        </a>
      </Tooltip>
    );
    setDropdownItems(newDropdownItems);
  }, [hierarchyAccounts, hierarchyAccounts.length]);

  useEffect(() => {
    document.title = "Arpari | Dashboard"
    getBalances(undefined, undefined, true, true)
    getCounts(undefined, undefined)
    backendGetRequest("/api/generic-tag/group/", { params: { page_index: 0, group_type: BA_TAG } })
      .then((res) => {
        setGroupsPageCountExpected(res?.data?.total_pages);
        for (let i = 1; i <= res?.data?.total_pages; i++) {
          backendGetRequestWithCache("api/generic-tag/group/", { params: { page_index: i, group_type: BA_TAG } },
            (cachedResponse) => {
              if (!cachedResponse) return;
              const cachedGroups = cachedResponse?.data?.generic_groups || [];
              setGroups(prevState => {
                const updatedGroups = prevState ? [...prevState] : [];
                cachedGroups.forEach(group => {
                  if (!updatedGroups.some(existingGroup => existingGroup.id === group.id)) {
                    updatedGroups.push(group);
                  }
                });
                return updatedGroups;
              });
            },
            (nonCachedResponse) => {
              const nonCachedGroups = nonCachedResponse?.data?.generic_groups || [];
              if (nonCachedGroups) {
                setGroups(prevState => {
                  const updatedGroups = prevState ? [...prevState] : [];
                  nonCachedGroups.forEach(group => {
                    const index = updatedGroups.findIndex(existingGroup => existingGroup.id === group.id);
                    if (index !== -1) {
                      updatedGroups[index] = group;
                    } else {
                      updatedGroups.push(group);
                    }
                  });
                  return updatedGroups;
                });
              }
              setGroupsPageCountReceived(prevState => prevState === -1 ? 1 : prevState + 1);
            },
            (err) => {
              setGroupsPageCountExpected(prevState => prevState - 1);
            }
          );
        }
      });
  }, []);

  useEffect(() => {
    if (balancesByPurposePageCountExpected && balancesByPurposePageCountReceived) {
      Object.keys(balancesByPurposePageCountExpected).forEach((purposeId) => {
        if (
          balancesByPurposePageCountExpected[purposeId] !== 0 &&
          balancesByPurposePageCountExpected[purposeId] === balancesByPurposePageCountReceived[purposeId]
        ) {
          setBalancesByPurposeIsComplete(prevState => ({
            ...prevState,
            [purposeId]: true
          }));
        }
      });
    }
  }, [balancesByPurposePageCountExpected, balancesByPurposePageCountReceived]);

  useEffect(() => {
    if (!accountsPageCountExpected) return;
    setAccountsLoading(accountsPageCountExpected > accountsPageCountReceived);
  }, [accountsPageCountExpected, accountsPageCountReceived]);

  useEffect(() => {
    if (!flattenedTagsPageCountExpected) return;
    setFlattenedTagsLoading(flattenedTagsPageCountExpected > flattenedTagsPageCountReceived);
  }, [flattenedTagsPageCountExpected, flattenedTagsPageCountReceived]);

  useEffect(() => {
    if (!groupsPageCountExpected) return;
    setGroupsLoading(groupsPageCountExpected > groupsPageCountReceived);
  }, [groupsPageCountExpected, groupsPageCountReceived]);

  useEffect(() => {
    if (!findNewAccountsPageCountExpected) return;
    setFindingAccounts(findNewAccountsPageCountExpected > findNewAccountsPageCountReceived);
  }, [findNewAccountsPageCountExpected, findNewAccountsPageCountReceived]);


  useEffect(() => {
    backendGetRequest("/api/generic-tag/", { params: { page_index: 0, group_type: BA_TAG } })
      .then((res) => {
        setFlattenedTagsPageCountExpected(res?.data?.total_pages);
        for (let i = 1; i <= res?.data?.total_pages; i++) {
          backendGetRequestWithCache("api/generic-tag/", { params: { page_index: i, group_type: BA_TAG } },
            (cachedResponse) => {
              const cachedTags = cachedResponse?.data?.generic_tags || [];
              if (!cachedTags) return
              setFlattenedTags(prevState => {
                const updatedTags = prevState ? [...prevState] : [];
                cachedTags.forEach(tag => {
                  if (!updatedTags.some(existingTag => existingTag.id === tag.id)) {
                    updatedTags.push(tag);
                  }
                });
                return updatedTags;
              });
            },
            (nonCachedResponse) => {
              const nonCachedTags = nonCachedResponse?.data?.generic_tags || [];
              setFlattenedTags(prevState => {
                const updatedTags = prevState ? [...prevState] : [];
                nonCachedTags.forEach(tag => {
                  const index = updatedTags.findIndex(existingTag => existingTag.id === tag.id);
                  if (index !== -1) {
                    updatedTags[index] = tag;
                  } else {
                    updatedTags.push(tag);
                  }
                });
                return updatedTags;
              });
              setFlattenedTagsPageCountReceived(prevState => prevState === -1 ? 1 : prevState + 1);
            },
            (err) => {
              setFlattenedTagsPageCountExpected(prevState => prevState - 1);
            }
          );
        }
      });
  }, []);

  useEffect(() => {
    if (accountsPerPage && typeof accountsPerPage === 'object') {
      const hierarchyAccounts = Object.values(accountsPerPage).flat();
      setHierarchyAccounts(hierarchyAccounts);
    }
  }, [accountsPerPage]);

  useEffect(() => {
    if (!countLoading && accountCount > 0 && accountCount < 50 && !currentGroupId && !currentTagId && !preventAutoFetch) {
      getBankAccounts(undefined, undefined);
    } else if (accountCount >= 50 && !currentGroupId && !currentTagId) {
      setHierarchyAccounts([]);
      setAccountsPerPage({});
    }
  }, [countLoading, accountCount, currentGroupId, currentTagId, preventAutoFetch]);

  // useEffect to handle refetch after clear
  useEffect(() => {
    if (shouldFetchAfterClear && !currentGroupId && !currentTagId) {
      getBalances(undefined, undefined, true, true);
      getCounts(undefined, undefined);
      setShouldFetchAfterClear(false);
    }
  }, [shouldFetchAfterClear, currentGroupId, currentTagId]);

  const getCounts = (groupId, tagId) => {
    setCountLoading(true)
    backendGetRequest("/api/bank-account/counts-by-tag/", { params: { group_id: groupId, tag_id: tagId } })
      .then((res) => {
        if (currentGroupId === groupId && currentTagId === tagId) {
          setAccountCount(res?.data?.accounts)
          setBankCount(res?.data?.banks)
          setCountLoading(false)
        }
      }).catch(() => {
        setCountLoading(false)
      });
  }

  const clearBalances = () => {
    const purposes = JSON.parse(window.localStorage.getItem("bankAccountPurposes")) || [];
    let initialZero = {}
    purposes.forEach((purpose) => {
      initialZero[purpose.id] = 0;
    });

    let initialNegOne = {}
    purposes.forEach((purpose) => {
      initialNegOne[purpose.id] = -1;
    });

    setBalancesByPurposeIsComplete({});
    setBalancesByPurpose(initialZero);
    setBalancesByPurposePageCountExpected(initialZero);
    setBalancesByPurposePageCountReceived(initialZero);
    setBalancesByPurpose(initialZero);
  };

  const getBalances = (groupId, tagId, quickGet, clearState) => {
    if (clearState) {
      clearBalances();
    }

    bankAccountPurposes.map((purpose) => {
      let purposeId = purpose.id
      let baseParams = {
        purpose_id: purposeId,
        group_id: groupId,
        tag_id: tagId,
        quick_get: quickGet,
        page_index: 0
      }
      backendGetRequest(
        "/api/bank-account/balances-by-purpose/",
        { params: { ...baseParams, page_index: 0 } }
      )
        .then((res) => {
          setBalancesByPurposePageCountExpected(prevState => ({
            ...prevState, [purposeId]: res?.data?.total_pages
          }));
          for (let page = 1; page <= res?.data?.total_pages; page++) {
            backendGetRequest("/api/bank-account/balances-by-purpose/", { params: { ...baseParams, page_index: page } })
              .then((res) => {
                if (currentGroupId === groupId && currentTagId === tagId) {
                  setBalancesByPurpose(prevState => ({
                    ...prevState, [purposeId]: ((prevState[purposeId] || 0) + (res?.data?.current_balance || 0))
                  }));
                  setBalancesByPurposePageCountReceived(prevState => ({
                    ...prevState, [purposeId]: (prevState[purposeId] || 0) + 1
                  }));
                }
              }).catch((error) => {
                console.error(`Failed to fetch balances for purpose ${purposeId}, page ${page}:`, error);
                setBalancesByPurposePageCountExpected(prevState => ({
                  ...prevState, [purposeId]: (prevState[purposeId] - 1)
                }));
              })
          }
        }).catch((error) => {
          if (currentGroupId === groupId && currentTagId === tagId) {
            console.error(`Failed to fetch balances for purpose ${purposeId}:`, error);
            setBalancesByPurposePageCountExpected(prevState => ({
              ...prevState, [purposeId]: (prevState[purposeId] - 1)
            }));
          }
        })
    })
  };

  const getBankAccounts = (groupId, tagId) => {
    setHierarchyAccounts([])
    setAccountsLoading(true)
    setAccountsPerPage({})
    setAccountsPageCountExpected(0)
    setAccountsPageCountReceived(-1)
    const baseParams = {}
    if (groupId !== undefined) baseParams.group_id = groupId;
    if (tagId !== undefined) baseParams.tag_id = tagId;
    backendGetRequest("/api/bank-account/", { params: { ...baseParams, ...{ page_index: 0 } } })
      .then((res) => {
        setAccountsPageCountExpected(prevState => res?.data?.total_pages);
        for (let i = 1; i <= res?.data?.total_pages; i++) {
          let params = { ...baseParams, ...{ page_index: i } }
          backendGetRequestWithCache("/api/bank-account/", { params: params },
            (cachedResponse) => {
              if (cachedResponse?.data) {
                setAccountsPerPage(prevState => {
                  if (!prevState[params.page_index]) {
                    return {
                      ...prevState,
                      [params.page_index]: (cachedResponse?.data?.data || []),
                    }
                  }
                  return prevState;
                });
              }
            },
            (nonCachedResponse) => {
              if (nonCachedResponse?.data) {
                setAccountsPerPage(prevState => ({
                  ...prevState,
                  [params.page_index]: (nonCachedResponse?.data?.data || []),
                }));
                setAccountsPageCountReceived(prevState => prevState === -1 ? 1 : prevState + 1);
              }
            },
            () => { // error handling
              setAccountsPageCountExpected(prevState => prevState - 1);
            }
          );
        }
      })
  }

  useEffect(() => {
    if (currentGroupId || currentTagId) {
      getCounts(currentGroupId, currentTagId)
      getBalances(currentGroupId, currentTagId, false, true)
      getBankAccounts(currentGroupId, currentTagId)
    }
  }, [currentGroupId, currentTagId]);

  useEffect(() => {
    if (currentGroupId !== undefined) {
      window.localStorage.setItem("currentGroupId", JSON.stringify(currentGroupId));
    }
  }, [currentGroupId]);

  useEffect(() => {
    if (currentTagId !== undefined) {
      window.localStorage.setItem("currentTagId", JSON.stringify(currentTagId));
    }
  }, [currentTagId]);

  // Reset preventAutoFetch when user selects something
  useEffect(() => {
    if (currentGroupId || currentTagId) {
      setPreventAutoFetch(false);
    }
  }, [currentGroupId, currentTagId]);

  const handleSelectionChange = (selectedValue) => {
    setSelectedDropdownValue(selectedValue);
  };

  const handleApply = async (tagId, groupId) => {
    cancelAllAxiosRequests()
    setCurrentGroupId(groupId)
    setCurrentTagId(tagId)
  };

  const handleClear = async () => {
    cancelAllAxiosRequests();
    
    // Clear current selections
    setCurrentGroupId(undefined);
    setCurrentTagId(undefined);
    
    // Clear localStorage
    window.localStorage.removeItem("currentGroupId");
    window.localStorage.removeItem("currentTagId");
    
    // Reset accounts state to initial values
    setHierarchyAccounts([]);
    setAccountsPerPage({});
    setAccountsPageCountExpected(0);
    setAccountsPageCountReceived(-1);
    setAccountsLoading(false);
    
    // Reset counts to initial values
    setAccountCount(0);
    setBankCount(0);
    setCountLoading(true);
    
    // Reset balance states to initial values
    setBalancesByPurposePageCountExpected({});
    setBalancesByPurposePageCountReceived({});
    setBalancesByPurpose({});
    setBalancesByPurposeIsComplete({});
    
    // Reset UI states to initial values
    setCsvModalVisible(false);
    
    // Set flags for controlled refetch
    setShouldFetchAfterClear(true);
    setPreventAutoFetch(true);
  };

  const handleFindNewAccounts = async () => {
    setFindNewAccountsPageCountExpected(0);
    setFindNewAccountsPageCountReceived(-1);
    
    try {
      const initialResponse = await backendPostWithoutRefresh('/api/bank-account/uncategorized/', {
        page_index: 0
      });
      
      const totalPages = initialResponse?.data?.total_pages || 0;
      setFindNewAccountsPageCountExpected(totalPages);
      
      if (totalPages === 0) {
        console.log("No new accounts found");
        return;
      }
      
      for (let page = 1; page <= totalPages; page++) {
        backendPostWithoutRefresh('/api/bank-account/uncategorized/', {
          page_index: page
        }).then((response) => {
          const pageAccounts = response?.data?.accounts || [];
          if (pageAccounts.length > 0) {
            setHierarchyAccounts(prevState => [...prevState, ...pageAccounts]);
            setAccountCount(prevCount => prevCount + pageAccounts.length);
          }
          setFindNewAccountsPageCountReceived(prevState => prevState === -1 ? 1 : prevState + 1);
        }).catch((error) => {
          console.error(`Failed to fetch new accounts for page ${page}:`, error);
          setFindNewAccountsPageCountExpected(prevState => prevState - 1);
          setFindNewAccountsPageCountReceived(prevState => prevState === -1 ? 1 : prevState + 1);
        });
      }
      
    } catch (error) {
      console.error("Failed to find new accounts:", error);
      setFindNewAccountsPageCountExpected(0);
    }
  };

  const deleteBankAccount = (accountId) => {
    setHierarchyAccounts(prevState => prevState.filter(account => account.id !== accountId));
  }

  const editBankAccount = (accountId, newDescription) => {
    setHierarchyAccounts(prevState =>
      prevState.map(account =>
        account.id === accountId
          ? { ...account, description: newDescription }
          : account
      )
    );
  }

  const reassignBankAccount = (updatedBankAccount) => {
    if (!updatedBankAccount?.generic_tags?.some(tag => tag.id === currentTagId)) {
      deleteBankAccount(updatedBankAccount.id);
      return;
    }
    setHierarchyAccounts(prevState =>
      prevState.map(account =>
        account.id === updatedBankAccount.id ? { ...updatedBankAccount } : account
      )
    );
  };

  return (
    <OuterBox>
      <Box sx={{ flexGrow: 1 }}>
        <Grid container spacing={2}>
          <Grid item xs={8}>
            <Box
              sx={{
                margin: (theme) => theme.spacing(0),
                backgroundColor: "white",
                border: (theme) => `1px solid ${theme.palette.secondary.light}`,
                borderRadius: 1,
                padding: (theme) => theme.spacing(1),
                minHeight: "100px",
                width: "100%",
                height: "100%"
              }}
            >
              <Typography variant="overline" fontSize={12}>Balance Breakdown</Typography>
              <Divider />
              <BalanceHeaderV3
                balancesByPurpose={balancesByPurpose}
                isComplete={balancesByPurposeIsComplete}
              />
            </Box>
          </Grid>
          <Grid item xs={4}>
            <Box
              sx={{
                margin: (theme) => theme.spacing(0),
                backgroundColor: "white",
                border: (theme) => `1px solid ${theme.palette.secondary.light}`,
                borderRadius: 1,
                padding: (theme) => theme.spacing(1),
                minHeight: "100px",
                width: "100%",
                height: "100%",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center"
              }}
            >
              <Stack
                direction="row"
                divider={<Divider orientation="vertical" flexItem />}
                sx={{
                  justifyContent: "space-evenly",
                  alignItems: "stretch",
                }}
                spacing={2}
              >
                <Stack direction="column" sx={{ alignItems: "center", justifyContent: "space-around" }}>
                  {countLoading ? <Skeleton width="100%" /> : <Typography variant="h1" color="primary">{accountCount}</Typography>}
                  <Typography color="secondary">Accounts</Typography>
                </Stack>
                <Stack direction="column" sx={{ alignItems: "center", justifyContent: "space-around" }}>
                  {countLoading ? <Skeleton width="100%" /> : <Typography variant="h1" color="primary">{bankCount}</Typography>}
                  <Typography color="secondary">Banks</Typography>
                </Stack>
              </Stack>
            </Box>
          </Grid>
        </Grid>
      </Box>
      <Box style={{ width: "100%" }}>
        <div style={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
        }}>
          <div style={{ flexGrow: 1, marginRight: 10 }}>
            <SelectBankAccountTag
              groups={groups}
              customTags={flattenedTags}
              loading={flattenedTagsLoading || groupsLoading || accountsLoading}
              handleApply={handleApply}
              currentTagId={currentTagId}
              currentGroupId={currentGroupId}
              includeUncategorized={true}
              multiSelect={false}
              showFindNewAccounts={selectedDropdownValue === 'group_-1'}
              onFindNewAccounts={handleFindNewAccounts}
              findingAccounts={findingAccounts}
              onSelectionChange={handleSelectionChange}
            />
          </div>
          <Button
            onClick={handleClear}
            style={{ marginRight: 10 }}
            sx={{
              fontSize: 14,
              textTransform: "none",
              width: "80px",
              paddingTop: 0.4,
              pb: 0.4,
            }}
            variant="outlined"
            color="secondary"
            disableElevation
          >
            Clear
          </Button>
          <GenericDropdown
            dropdownItems={dropdownItems}
          />
          <ExportHierarchyAccount
            tableData={hierarchyAccounts}
            csvModalVisible={csvModalVisible}
            handleCancelModal={() => setCsvModalVisible(false)}
          />
        </div>
      </Box>
      <BankAccountMultiView
        allowActions={true}
        accountsInfo={hierarchyAccounts}
        setAccounts={setHierarchyAccounts}
        accountOwnerType="Hierarchy"
        accountTypeTitle={"Account"}
        newAccountType="checking"
        accountOwnerAppName="hierarchy"
        accountTypeAttribute={"hierarchy_bank_accounts"}
        deleteBankAccount={deleteBankAccount}
        editBankAccount={editBankAccount}
        reassignBankAccount={reassignBankAccount}
        loadingTable={accountsLoading}
      />
    </OuterBox>
  );
}

export default BankAccountHierarchy;
